import { useCallback, useEffect, useRef } from 'react';

import { GizmoHelper, GizmoViewport, Line, OrbitControls } from '@react-three/drei';
import { Canvas, useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { black02, green04 } from '../../theme.ts';
import { ModelFile, ModelLayer } from '../model-context/ModelContext.ts';
import { LayerSetting } from '../project-settings/types.ts';
import CustomGrid from '../workspace/scene/CustomGrid.tsx';
import { ModelPreviewContent } from './ModelPreviewContent.tsx';
import { RightClickHandler } from './RightClickHandler.tsx';
import { ScalePickOverlay } from './ScalePickOverlay.tsx';

interface SceneProps {
  onObjectHover: (name: string | null) => void;
  onObjectClick: (name: string) => void;
  /** Left-click selection callback (optional). When provided, left-click selects; right-click opens popup. */
  onObjectSelect?: (name: string) => void;
  hoveredLayer: string | null;
  selectedLayer?: string | null;
  layerSettings: LayerSetting[];
  hoveredLayerColor: string | null;
  uploadedModels?: ModelFile[] | null;
  onLayersFound?: (modelName: string, layers: ModelLayer[]) => void;
  visibleLayers?: string[];
  loadedModels?: Record<string, boolean>;
  isCapturing?: boolean;
  onCaptureReady?: (captureFunc: () => string) => void;
  /** When true, clicks on the model pick points for scale calibration (disables orbit). */
  scalePickActive?: boolean;
  onScalePointsPicked?: (distanceUnits: number) => void;
  onScalePickCancel?: () => void;
  /** Uniform scale factor applied to uploaded models (used in step 2 to preview real-world size). */
  modelScale?: number;
}

// Component to force re-render when isCapturing changes
function ForceRender({ isCapturing }: { isCapturing: boolean }) {
  const { invalidate } = useThree();

  useEffect(() => {
    invalidate();
  }, [isCapturing, invalidate]);

  return null;
}

// Component to handle capturing only the model content
function CaptureHandler({
  onCaptureReady,
  isCapturing,
}: {
  onCaptureReady?: (captureFunc: () => string) => void;
  isCapturing: boolean;
}) {
  const { gl, scene, camera } = useThree();
  const captureRef = useRef<() => string>();

  const captureImage = useCallback(() => {
    // Store original background
    const originalBackground = scene.background;

    // Remove the background for the capture (set to null)
    scene.background = null;

    // Store visibility of helper objects
    const hiddenObjects: THREE.Object3D[] = [];

    scene.traverse((object) => {
      // Hide CustomGrid using its explicit identifiers to avoid affecting other LineSegments objects
      const shouldHide = (object.userData && object.userData.isGrid) || object.name === 'CustomGrid';

      if (shouldHide && object.visible) {
        hiddenObjects.push(object);
        object.visible = false;
      }
    });

    // Render the scene
    gl.render(scene, camera);

    // Capture the image
    const dataURL = gl.domElement.toDataURL('image/png', 1.0);
    const base64 = dataURL.split(',')[1];

    // Restore visibility
    hiddenObjects.forEach((obj) => {
      obj.visible = true;
    });

    // Restore original background
    scene.background = originalBackground;

    return base64;
  }, [gl, scene, camera]);

  useEffect(() => {
    captureRef.current = captureImage;
    if (onCaptureReady) {
      onCaptureReady(captureImage);
    }
  }, [captureImage, onCaptureReady]);

  // When isCapturing becomes true, trigger a render and capture
  useEffect(() => {
    if (isCapturing && captureRef.current) {
      // Force a render cycle
      gl.render(scene, camera);
    }
  }, [isCapturing, gl, scene, camera]);

  return null;
}

export function ModelPreviewScene({
  onObjectHover,
  onObjectClick,
  onObjectSelect,
  hoveredLayer,
  selectedLayer,
  layerSettings,
  hoveredLayerColor,
  uploadedModels,
  onLayersFound,
  visibleLayers,
  loadedModels,
  isCapturing = false,
  onCaptureReady,
  scalePickActive = false,
  onScalePointsPicked,
  onScalePickCancel,
  modelScale = 1,
}: SceneProps) {
  // Use default camera settings
  const position: [number, number, number] = [35, 35, 35];
  const fov = 70;

  function BackgroundColor() {
    const { scene } = useThree();

    useEffect(() => {
      scene.background = new THREE.Color(black02); // tamna siva
    }, [scene]);

    return null;
  }

  const AXIS_LENGTH = 40.0;

  function CustomAxesGizmo() {
    const lineProps = {
      lineWidth: 5,
      renderOrder: 1,
    };

    const DashedLine = ({
      start,
      end,
      color,
    }: {
      start: [number, number, number];
      end: [number, number, number];
      color: string;
    }) => <Line points={[start, end]} color={color} dashed={true} dashScale={0.05} {...lineProps} lineWidth={1.5} />;

    return (
      <group scale={[1, 1, 1]}>
        <DashedLine start={[-AXIS_LENGTH / 4, 0, 0]} end={[-AXIS_LENGTH, 0, 0]} color={green04} />
        <DashedLine start={[0, -AXIS_LENGTH / 4, 0]} end={[0, -AXIS_LENGTH, 0]} color={green04} />
        <DashedLine start={[0, 0, -AXIS_LENGTH / 4]} end={[0, 0, -AXIS_LENGTH]} color={green04} />
      </group>
    );
  }

  return (
    <Canvas
      gl={{
        preserveDrawingBuffer: true,
        antialias: true,
        alpha: true, // Set to true to ensure background color is NOT included in image
      }}
      camera={{ position, fov }} // Dynamic camera settings based on model size
      style={{
        background: 'transparent',
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    >
      <BackgroundColor />
      <ForceRender isCapturing={isCapturing} />
      <CaptureHandler onCaptureReady={onCaptureReady} isCapturing={isCapturing} />

      {!isCapturing && <CustomGrid />}

      {!isCapturing && (
        <GizmoHelper alignment="bottom-left" margin={[80, 80]}>
          <GizmoViewport hideNegativeAxes={true} axisColors={['#ADFF2F', '#ADFF2F', '#ADFF2F']} axisHeadScale={0.8} />
          <CustomAxesGizmo />
        </GizmoHelper>
      )}

      <ModelPreviewContent
        onObjectHover={onObjectHover}
        onObjectClick={onObjectClick}
        onObjectSelect={onObjectSelect}
        hoveredLayer={hoveredLayer}
        selectedLayer={selectedLayer}
        layerSettings={layerSettings}
        hoveredLayerColor={hoveredLayerColor}
        uploadedModels={uploadedModels}
        onLayersFound={onLayersFound}
        visibleLayers={visibleLayers}
        loadedModels={loadedModels}
        modelScale={modelScale}
      />

      <RightClickHandler onObjectClick={onObjectClick} />
      <OrbitControls makeDefault target={[0, 0, 0]} enableDamping={false} enabled={!scalePickActive} />

      <ScalePickOverlay
        active={scalePickActive}
        onTwoPointsPicked={(_p1, _p2, dist) => onScalePointsPicked?.(dist)}
        onCancel={onScalePickCancel}
      />
    </Canvas>
  );
}
